import { useEffect, useState } from "react";
import { useClub } from "@/contexts/ClubContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const { activeClub, clubs, getClubAccent, setActiveClub } = useClub();
  usePageTitle("Galerie");
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { imgs, idx, title, description, club_id }

  useEffect(() => {
    const params = activeClub ? `?club_id=${activeClub}` : "";
    axios.get(`${API}/gallery${params}`).then(r => setGallery(r.data)).catch(() => {});
  }, [activeClub]);

  const openLightbox = (imgs, idx, item) => {
    setLightbox({ imgs, idx, title: item.title, description: item.description, club_id: item.club_id });
  };

  const navLightbox = (dir) => {
    if (!lightbox) return;
    const next = lightbox.idx + dir;
    if (next >= 0 && next < lightbox.imgs.length) {
      setLightbox({ ...lightbox, idx: next });
    }
  };

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="gallery-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
          Foto<span style={{ color: activeClub ? getClubAccent(activeClub) : '#fbbf24' }}>galerie</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-8">Eindrücke aus dem Ninersbillard</p>

        {/* Club Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-club-filter">
          <button onClick={() => setActiveClub(null)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${!activeClub ? 'border-primary bg-primary text-primary-foreground' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
            data-testid="gallery-filter-all">
            Alle
          </button>
          {clubs.map(club => (
            <button key={club.id} onClick={() => setActiveClub(activeClub === club.id ? null : club.id)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${activeClub === club.id ? '' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
              style={activeClub === club.id ? { borderColor: club.accent, backgroundColor: club.accent, color: '#09090b' } : {}}
              data-testid={`gallery-filter-${club.id}`}>
              {club.name}
            </button>
          ))}
        </div>

        {gallery.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">Noch keine Fotos vorhanden. Die Admins können über das Dashboard Bilder hinzufügen.</p>
        ) : (
          <div className="space-y-10">
            {gallery.map((item, i) => {
              const imgs = item.image_urls?.length ? item.image_urls : (item.image_url ? [item.image_url] : []);
              if (!imgs.length) return null;
              return (
                <div key={item.id} className={`animate-fade-in-up stagger-${(i % 5) + 1}`} data-testid={`gallery-item-${item.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge style={{ backgroundColor: getClubAccent(item.club_id), color: '#09090b' }}
                      className="text-xs font-bold uppercase">
                      {clubs.find(c => c.id === item.club_id)?.name}
                    </Badge>
                    <h3 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{item.title}</h3>
                  </div>
                  {item.description && <p className="text-sm text-muted-foreground mb-3">{item.description}</p>}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {imgs.map((url, idx) => (
                      <div key={`${item.id}-${url}`}
                        className="group relative overflow-hidden bg-card border border-white/5 cursor-pointer card-hover"
                        onClick={() => openLightbox(imgs, idx, item)}
                        data-testid={`gallery-img-${item.id}-${idx}`}>
                        <div className="aspect-square overflow-hidden">
                          <img src={resolveImageUrl(url)} alt={`${item.title} ${idx + 1}`} className="w-full h-full object-cover img-zoom" onError={e => { e.target.parentElement.parentElement.style.display = 'none'; }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
          <DialogContent className="max-w-4xl bg-card border-white/10 p-0 overflow-hidden">
            <DialogTitle className="sr-only">{lightbox?.title || 'Foto'}</DialogTitle>
            {lightbox && (
              <div>
                <div className="relative">
                  <img src={resolveImageUrl(lightbox.imgs[lightbox.idx])} alt={lightbox.title} className="w-full max-h-[70vh] object-contain" />
                  {lightbox.imgs.length > 1 && (
                    <>
                      {lightbox.idx > 0 && (
                        <button onClick={() => navLightbox(-1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 transition-colors"
                          data-testid="lightbox-prev">
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                      )}
                      {lightbox.idx < lightbox.imgs.length - 1 && (
                        <button onClick={() => navLightbox(1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 transition-colors"
                          data-testid="lightbox-next">
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      )}
                      <span className="absolute bottom-2 right-3 text-xs bg-black/60 px-2 py-0.5">
                        {lightbox.idx + 1} / {lightbox.imgs.length}
                      </span>
                    </>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge style={{ backgroundColor: getClubAccent(lightbox.club_id), color: '#09090b' }}
                      className="text-xs font-bold uppercase">
                      {clubs.find(c => c.id === lightbox.club_id)?.name}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{lightbox.title}</h3>
                  {lightbox.description && <p className="text-sm text-muted-foreground mt-1">{lightbox.description}</p>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
